import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getActivePolicy,
  listAuditExceptions,
  listMemos,
  listSecurities,
  topScores,
} from "@/lib/api";
import type { SecuritySummary } from "@/lib/api/types";
import { fmtDate, fmtDateTime, fmtScore, relativeAge } from "@/lib/format";
import { Panel } from "@/components/ui/Panel";
import { Stat } from "@/components/ui/Stat";
import { DataTable } from "@/components/ui/Table";
import {
  ConfidenceBadge,
  ExceptionSeverityBadge,
  ThesisBadge,
} from "@/components/ui/Badges";
import { EmptyState, LoadingState } from "@/components/ui/States";
import { Pill } from "@/components/ui/Pill";
import { useActiveWeightVector } from "@/hooks/useActiveWeightVector";

export function DashboardPage() {
  const { activeId } = useActiveWeightVector();

  const universe = useQuery({
    queryKey: ["securities", "all", activeId],
    queryFn: () =>
      // 2000 covers the full active universe (S&P 500 + Stoxx 600 + Nikkei 225
      // + HSI + ETFs + crypto ≈ 1,243). The previous limit of 500 silently
      // dropped half the universe and made the "39 scored / 500" KPI on the
      // dashboard meaningless. The API endpoint caps at limit=2000.
      listSecurities({ limit: 2000, weightVectorId: activeId }),
  });
  const top = useQuery({
    // topScores requires a number — skip until active vector resolves.
    enabled: activeId !== undefined,
    queryKey: ["scoring", "top", activeId],
    queryFn: () => topScores(activeId!, 12),
    retry: false,
  });
  const memos = useQuery({
    queryKey: ["memos", "recent"],
    queryFn: () => listMemos(8),
  });
  const exceptions = useQuery({
    queryKey: ["audit", "exceptions", "recent"],
    queryFn: () => listAuditExceptions({ limit: 8 }),
  });
  const activePolicy = useQuery({
    queryKey: ["policy", "active"],
    queryFn: getActivePolicy,
  });

  const universeRows = universe.data ?? [];
  const scored = universeRows.filter((s) => s.latest_score !== null && s.latest_score !== undefined);
  const insufficient = universeRows.filter((s) => s.latest_confidence === "insufficient");
  const improving = universeRows.filter((s) => s.latest_thesis === "improving").length;
  const deteriorating = universeRows.filter((s) => s.latest_thesis === "deteriorating").length;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-2xs text-foreground-muted">
          Active policy:{" "}
          {activePolicy.data?.name ? (
            <span className="text-foreground-secondary">{activePolicy.data.name} v{activePolicy.data.version}</span>
          ) : (
            <span>none — seed via the Policy page</span>
          )}
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Panel><Stat label="Universe" value={universeRows.length} /></Panel>
        <Panel>
          <Stat
            label="Scored"
            value={scored.length}
            hint={`${scored.length}/${universeRows.length} have a recent composite`}
          />
        </Panel>
        <Panel>
          <Stat
            label="Improving"
            value={improving}
            tone="good"
            hint={`vs ${deteriorating} deteriorating`}
          />
        </Panel>
        <Panel>
          <Stat
            label="Insufficient quality"
            value={insufficient.length}
            tone={insufficient.length > 0 ? "warn" : "default"}
          />
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          className="lg:col-span-2"
          title="Top composites"
          description="Sorted by composite score under the active weight vector."
        >
          {top.isLoading && <LoadingState message="Loading top scores…" />}
          {top.isError && (
            <EmptyState
              title="No scores yet"
              description="Run scoring under a weight vector first. Use the Screener to seed inputs."
            />
          )}
          {top.data && top.data.length > 0 && (
            <DataTable
              dense
              rows={top.data}
              rowKey={(r) => r.security_id}
              columns={[
                {
                  key: "ticker",
                  header: "Ticker",
                  cell: (r) => (
                    <Link
                      to={`/company/${r.security_id}`}
                      className="font-medium text-foreground-primary hover:text-accent-strong"
                    >
                      {r.ticker}
                    </Link>
                  ),
                },
                {
                  key: "composite",
                  header: "Composite",
                  cell: (r) => <span className="tabular-nums">{fmtScore(r.composite)}</span>,
                  align: "right",
                },
                {
                  key: "confidence",
                  header: "Conf.",
                  cell: (r) => <ConfidenceBadge band={r.confidence_band} />,
                },
                {
                  key: "thesis",
                  header: "Thesis",
                  cell: (r) => <ThesisBadge status={r.thesis_status} />,
                },
                {
                  key: "as_of",
                  header: "As of",
                  cell: (r) => fmtDate(r.as_of),
                  align: "right",
                },
              ]}
              empty="No top scores."
            />
          )}
          {top.data && top.data.length === 0 && (
            <EmptyState
              title="Nothing scored yet"
              description="Once scores land for the active weight vector this panel populates automatically."
            />
          )}
        </Panel>

        <Panel
          title="Recent memos"
          description="Auto-generated, evidence-linked artifacts."
        >
          {memos.isLoading && <LoadingState />}
          {memos.data && memos.data.length === 0 && (
            <EmptyState title="No memos yet" />
          )}
          {memos.data && memos.data.length > 0 && (
            <ul className="space-y-2 text-sm">
              {memos.data.map((m) => (
                <li
                  key={m.id}
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-border-subtle pb-2 last:border-0"
                >
                  <div className="space-y-0.5">
                    <Link
                      to={`/company/${m.security_id}#memo`}
                      className="text-sm font-medium text-foreground-primary hover:text-accent-strong"
                    >
                      {m.ticker ?? m.title}
                    </Link>
                    <p className="text-2xs text-foreground-muted">
                      {fmtDateTime(m.created_at)}
                    </p>
                  </div>
                  <Pill tone={m.ai_assisted ? "accent" : "muted"}>
                    {m.ai_assisted ? "AI" : "template"}
                  </Pill>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          className="lg:col-span-2"
          title="Names needing review"
          description="Universe entries with insufficient confidence or deteriorating thesis."
        >
          {universe.isLoading && <LoadingState />}
          {universe.data && (
            <NeedsReviewTable rows={universeRows} />
          )}
        </Panel>

        <Panel
          title="Recent exceptions"
          description="Pipeline anomalies surfaced for review."
        >
          {exceptions.isLoading && <LoadingState />}
          {exceptions.data && exceptions.data.length === 0 && (
            <EmptyState title="No exceptions" description="Pipelines are healthy." />
          )}
          {exceptions.data && exceptions.data.length > 0 && (
            <ul className="space-y-2 text-sm">
              {exceptions.data.map((ex) => (
                <li
                  key={ex.id}
                  className="space-y-0.5 border-b border-border-subtle pb-2 last:border-0"
                >
                  <div className="flex items-center justify-between gap-2">
                    <ExceptionSeverityBadge severity={ex.severity} />
                    <span className="text-2xs text-foreground-muted">
                      {relativeAge(ex.ts)}
                    </span>
                  </div>
                  <p className="font-mono text-2xs text-foreground-secondary">
                    {ex.service}
                  </p>
                  <p className="text-xs text-foreground-secondary">{ex.message}</p>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function NeedsReviewTable({ rows }: { rows: SecuritySummary[] }) {
  const filtered = rows
    .filter(
      (r) =>
        r.latest_confidence === "insufficient" ||
        r.latest_thesis === "deteriorating",
    )
    .sort((a, b) => (a.latest_score ?? 0) - (b.latest_score ?? 0))
    .slice(0, 12);
  if (filtered.length === 0) {
    return <EmptyState title="Nothing flagged" description="Universe is healthy under the current weight vector." />;
  }
  return (
    <DataTable
      dense
      rows={filtered}
      rowKey={(r) => r.id}
      columns={[
        {
          key: "ticker",
          header: "Ticker",
          cell: (r) => (
            <Link
              to={`/company/${r.id}`}
              className="font-medium text-foreground-primary hover:text-accent-strong"
            >
              {r.ticker}
            </Link>
          ),
        },
        {
          key: "name",
          header: "Name",
          cell: (r) => <span className="text-foreground-secondary">{r.name}</span>,
        },
        {
          key: "score",
          header: "Composite",
          cell: (r) => fmtScore(r.latest_score),
          align: "right",
        },
        {
          key: "confidence",
          header: "Confidence",
          cell: (r) =>
            r.latest_confidence ? <ConfidenceBadge band={r.latest_confidence} /> : "—",
        },
        {
          key: "thesis",
          header: "Thesis",
          cell: (r) =>
            r.latest_thesis ? <ThesisBadge status={r.latest_thesis} /> : "—",
        },
      ]}
    />
  );
}

